import { convertErrorToLoginError } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { ErrorMessage } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/ErrorMessage";
import { LoginError } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/styled";
import { MIN_PASSWORD_LENGTH } from "@faire/deprecated-retailer-visitor-shared/lib/consts";
import { handleKeyDown } from "@faire/deprecated-retailer-visitor-shared/lib/handleKeyDown";
import changePassword from "@faire/web-api--source/endpoints/www/api/user/change-password/post";
import validateResetPasswordToken from "@faire/web-api--source/endpoints/www/api/v2/users/validate-reset-password-token/token/post";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/slate/components/button";
import { LoadingSpinner } from "@faire/web/slate-deprecated/LoadingSpinner";
import { Input } from "@faire/web/slate-deprecated/Input";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useState, useEffect } from "react";
import styled from "styled-components";

interface IProps {
  onReset: () => void;
  resetToken: string | undefined;
}

export const ResetPassword: FC<IProps> = ({ onReset, resetToken }) => {
  const { strictLocalize } = useStrictLocalization();
  const [isValidatingResetPasswordToken, setIsValidatingResetPasswordToken] =
    useState(true);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<LoginError>();

  useEffect(() => {
    if (!resetToken) {
      setError(
        new LoginError(
          strictLocalize({
            id: "PW9vUd",
            defaultMessage: "Invalid reset token",
            description: {
              text: "Error shown to user when trying to reset password with an invalid token",
            },
          })
        )
      );
      setIsValidatingResetPasswordToken(false);
      return;
    }
    validateResetPasswordToken(resetToken)
      .catch((e) => setError(convertErrorToLoginError(e, strictLocalize)))
      .finally(() => setIsValidatingResetPasswordToken(false));
  }, [resetToken, strictLocalize]);

  const onContinue = async () => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(
        new LoginError(
          strictLocalize({
            id: "WAzvY+",
            defaultMessage: "Password needs at least 8 characters",
            description: { text: "Prompt for user to create a valid password" },
          })
        )
      );
      return;
    }

    try {
      await changePassword({
        new_password: password,
        reset_password_token: resetToken,
      });
      onReset();
    } catch (e) {
      setError(convertErrorToLoginError(e, strictLocalize));
    }
  };

  return (
    <LoginWrapper>
      {isValidatingResetPasswordToken ? (
        <StyledLoadingSpinner data-test-id="spinner" />
      ) : (
        <>
          <Typography
            variant="pageHeaderSerifRegular"
            data-test-id="reset-password-title"
          >
            <StrictLocalMsg
              id="aK92jA"
              defaultMessage="Reset your password"
              description={{
                text: "Allow users to reset a forgotten password",
              }}
            />
          </Typography>
          <Spacer height="1x" />
          <Typography>
            <StrictLocalMsg
              id="p24Pru"
              defaultMessage="Type your new password to receive access to Faire"
            />
          </Typography>
          <Spacer height="3x" />
          <Input
            autoFocus
            name="password"
            placeholder={strictLocalize({
              id: "IVu+DN",
              defaultMessage: "New Password",
              description: {
                text: "Placeholder text for users to enter a new password",
              },
            })}
            autoComplete="new-password"
            data-test-id="password"
            type="password"
            role="password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
            onKeyDown={handleKeyDown(onContinue)}
          />
          <Spacer height="4x" />
          <Button onClick={onContinue} data-test-id="continueButton">
            <StrictLocalMsg
              id="SkPsmn"
              defaultMessage="Reset Password"
              description={{
                text: "Call to action to reset password to newly entered password",
              }}
            />
          </Button>
          <ErrorMessage error={error} />
        </>
      )}
    </LoginWrapper>
  );
};

const StyledLoadingSpinner = styled(LoadingSpinner)`
  align-self: center;
`;
